/**
 * Host → brand, in ONE place, because the client and the server were each
 * deriving it independently and had already drifted.
 *
 * The client's `brandFromHost` knew about LimeCall and (once Handset shipped)
 * Handset. The server's signup path carried its own `/limecall\./i.test(origin)`
 * one-liner and knew only about LimeCall — so a Handset signup would have
 * created an org branded `limephone`, and every email that account ever
 * received would have come from the wrong product.
 *
 * That is the failure this module exists to prevent: the value written to
 * `organizations.product` at signup and the value the UI skins itself with have
 * to be the same function of the same hostname.
 *
 * Theming and sender identity only. Feature access is plan-driven — see
 * shared/accessControl.ts — and must never be derived from a hostname.
 */

export type BrandProduct = "limephone" | "limecall" | "outbound" | "handset";

/** Exact hosts, checked first. Cheaper to reason about than a pattern. */
const HOST_BRAND: Record<string, BrandProduct> = {
  "app.limecall.com": "limecall",
};

/**
 * Shape matchers for brands whose hostnames are not all enumerable yet.
 *
 * Every pattern MUST anchor the brand to a label boundary (`(^|\.)`) and to the
 * end of the host (`$`). Without both, `nothandset.com` matches, and so does an
 * attacker-controlled `handset.com.evil.net` — which would hand a hostile
 * origin the brand's sender identity and skin.
 */
const HOST_PATTERNS: ReadonlyArray<{ re: RegExp; brand: BrandProduct }> = [
  { re: /(^|\.)limecall\.com$/, brand: "limecall" },
  // Handset has no production hostname yet: app.handset.<tld> plus the Vercel
  // deployments in use today. Move these into HOST_BRAND once DNS exists.
  { re: /(^|\.)handset(-[a-z0-9]+)?\.(com|io|dev|app|vercel\.app)$/, brand: "handset" },
];

/**
 * The brand a hostname belongs to, or null for the default (LimePhone) host so
 * callers can no-op. Accepts a bare hostname, or an Origin/Referer header —
 * headers arrive as full URLs and callers should not each have to remember to
 * strip the scheme and path.
 */
export function brandFromHost(input?: string | null): BrandProduct | null {
  const raw = String(input ?? "").trim().toLowerCase();
  if (!raw) return null;

  // Tolerate "https://app.limecall.com/signup?x=1" as well as "app.limecall.com".
  const host = raw
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .split("/")[0]
    .split("?")[0]
    .split("#")[0]
    .replace(/:\d+$/, "")
    .replace(/\.$/, "");
  if (!host) return null;

  if (host in HOST_BRAND) return HOST_BRAND[host];
  for (const { re, brand } of HOST_PATTERNS) {
    if (re.test(host)) return brand;
  }
  return null;
}

/**
 * The value to write to `organizations.product` for a signup from this origin.
 * Unlike `brandFromHost` this never returns null — a new org must carry a
 * concrete brand from its first row, or its first emails default to the wrong
 * product.
 */
export function signupBrandFromOrigin(origin?: string | null): BrandProduct {
  return brandFromHost(origin) ?? "limephone";
}
